import React from 'react';
import { CardContent, styled, Box, Typography } from '@mui/material';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { PlanSummary } from '../types';
import FeatureFlag from '../../common/components/FeatureFlag';
import { FEATURES } from '../../account/api/getFeaturesForUserId';

const Row = styled(Box)({
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    justifyContent: 'space-between',
    paddingRight: 8,
    paddingLeft: 8
});
const ItemLabel = styled(Typography)({ flex: 1 });
const Price = styled(Typography)({ flex: 1, textAlign: 'right' });

const BoldLabel = styled(ItemLabel)({ fontWeight: 700 });
const BoldPrice = styled(Price)({ fontWeight: 700 });

const Divider = styled(Box)({
    height: '0px',
    border: 1,
    borderColor: '#EEE',
    borderStyle: 'solid',
    flexShrink: 0,
    marginTop: 20,
    marginBottom: 20,
    marginRight: 8,
    marginLeft: 8
});

interface Props {
    maxAthleteCount: number;
    planSummary: PlanSummary;
    showHeader: boolean;
}

export default function PaymentSummary({ maxAthleteCount, planSummary: planPreview, showHeader }: Props) {
    const {
        coachLicenseAmount,
        proratedCoachLicenseAmount,
        assistantCoachLicenseCount,
        assistantCoachLicenseAmount,
        subtotalDueNextBillingDate,
        taxAmountDueNextBillingDate,
        proratedCoachLicenseTaxAmount,
        hasProratedBilling,
        totalDueNextBillingDate,
        totalDueToday,
        nextBillingDate,
        hasTaxError
    } = planPreview;

    const formatNextBillingDate = () => {
        const date = moment(nextBillingDate);
        return date.isValid() ? date.format('MMMM Do, YYYY') : null;
    };

    const generateTaxRowText = () => {
        if (taxAmountDueNextBillingDate === null || hasTaxError) {
            return 'Calculating Taxes';
        }
        return hasProratedBilling
            ? `$${proratedCoachLicenseTaxAmount?.toFixed(2)}`
            : `$${taxAmountDueNextBillingDate?.toFixed(2)}`;
    };

    return (
        <>
            {showHeader && (
                <Typography variant="h5" marginX={1} marginBottom={3}>
                    Summary
                </Typography>
            )}
            <CardContent
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 0
                }}
            >
                <Row data-testid="athletes-range-row">
                    <Box flexDirection="column" display="flex" flex={1}>
                        <Box display="flex" flex={1} flexDirection="row" justifyContent="space-between">
                            <ItemLabel variant="subtitle2">{`Up to ${maxAthleteCount} Athletes`}</ItemLabel>
                            <Price variant="subtitle2">{`$${coachLicenseAmount}/month`}</Price>
                        </Box>
                        {hasProratedBilling && (
                            <FeatureFlag
                                feature={FEATURES.AVALARA_TAX_INTEGRATION_V1}
                                fallback={
                                    <Typography variant="caption" display="flex" sx={{ textWrap: 'pretty' }}>
                                        {`You will be charged an additional $${proratedCoachLicenseAmount.toFixed(
                                            2
                                        )} for this upgrade for the remainder of this billing cycle.`}
                                    </Typography>
                                }
                            >
                                <Typography variant="caption" display="flex" sx={{ textWrap: 'pretty' }}>
                                    {`You will be charged an additional $${proratedCoachLicenseAmount.toFixed(
                                        2
                                    )} plus taxes for this upgrade for the remainder of this billing cycle.`}
                                </Typography>
                            </FeatureFlag>
                        )}
                    </Box>
                </Row>
                <Divider />
                {assistantCoachLicenseCount > 0 && (
                    <>
                        <Row data-testid="assistant-coaches-row">
                            <ItemLabel variant="subtitle2">
                                {`${assistantCoachLicenseCount} Assistant ${
                                    assistantCoachLicenseCount === 1 ? 'Coach' : 'Coaches'
                                }`}
                            </ItemLabel>
                            <Price variant="subtitle2">{`$${assistantCoachLicenseAmount}/month`}</Price>
                        </Row>
                        <Divider />
                        <Row data-testid="subtotal-row">
                            <ItemLabel variant="subtitle2">Subtotal</ItemLabel>
                            <Price variant="subtitle2">{`$${subtotalDueNextBillingDate}/month`}</Price>
                        </Row>
                        <Divider />
                    </>
                )}
                <FeatureFlag feature={FEATURES.AVALARA_TAX_INTEGRATION_V1}>
                    <Row data-testid="tax-row">
                        <ItemLabel variant="subtitle2">Tax</ItemLabel>
                        <Price variant="subtitle2">{generateTaxRowText()}</Price>
                    </Row>
                    <Divider />
                </FeatureFlag>
                <Row data-testid="total-row">
                    <BoldLabel variant="subtitle2">Total</BoldLabel>
                    <BoldPrice variant="subtitle2">{`$${totalDueToday.toFixed(2)}`}</BoldPrice>
                </Row>
                {hasProratedBilling && (
                    <>
                        <Divider />
                        <Row data-testid="total-next-billing-cycle-row">
                            <Box flexDirection="column" display="flex" flex={1}>
                                <Box display="flex" flex={1} flexDirection="row" justifyContent="space-between">
                                    <ItemLabel variant="subtitle2">Total Due Next Billing Cycle</ItemLabel>
                                    <Price variant="subtitle2">{`$${totalDueNextBillingDate}`}</Price>
                                </Box>
                                {proratedCoachLicenseAmount > 0 && (
                                    <Typography variant="caption" display="flex" alignSelf="flex-end" textAlign="right">
                                        {`Starting ${formatNextBillingDate()}`}
                                    </Typography>
                                )}
                            </Box>
                        </Row>
                    </>
                )}
            </CardContent>
        </>
    );
}
